/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import MenuItem from '@mui/material/MenuItem'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import InputBase from '@mui/material/InputBase'
import SearchIcon from '@mui/icons-material/Search'
import Title from '../../component/Title'
import ResetFilterButton from '../../component/ResetFilterButton'
import { LIST_FILTER_SEARCH_FIELD_SX, LIST_FILTER_SEARCH_BUTTON_SX, ListFilterBar, ListFilterForm } from '../../component/ListFilterLayout'
import ListEmptyState from '../../component/ListEmptyState'
import ListTableContainer from '../../component/ListTableContainer'
import StatusChip, { ORDER_CLAIM_STATUS_CHIP } from '../../component/StatusChip'
import API from '../../component/Axios'
import Pagination from '../../component/Pagination'
import EditIcon from '../../component/EditIcon'
import ListActionGroup from '../../component/ListActionGroup'
import Snackbars from '../../component/Snackbars'
import Spiner from '../../component/Spiner'
import { buildListSearch as buildListSearchQuery, createInitialListState, parseNumberParam } from '../../utils/listPageState'
import { formatNumber } from '../../utils/numberDisplay'

const DEFAULT_SEARCH_SELECT = {
    searchClaimType: 0,
    searchStatus: 0,
}

const SEARCH_SELECT_PARAMS = {
    searchClaimType: {
        param: 'claimType',
        defaultValue: DEFAULT_SEARCH_SELECT.searchClaimType,
        parse: (value, defaultValue) => parseNumberParam(value, defaultValue),
    },
    searchStatus: {
        param: 'status',
        defaultValue: DEFAULT_SEARCH_SELECT.searchStatus,
        parse: (value, defaultValue) => parseNumberParam(value, defaultValue),
    },
}

const getInitialListState = (location) => {
    return createInitialListState({
        location,
        defaultSearchSelect: DEFAULT_SEARCH_SELECT,
        selectParams: SEARCH_SELECT_PARAMS,
    })
}

const buildListSearch = ({ curPage, searchQuery, searchSelect }) => {
    return buildListSearchQuery({
        curPage,
        searchQuery,
        searchSelect,
        selectParams: SEARCH_SELECT_PARAMS,
    })
}

const getClaimTypeColor = (claimType) => {
    switch (Number(claimType)) {
        case 2:
            return 'warning'
        case 3:
            return 'info'
        default:
            return 'default'
    }
}

const getPaymentMethodLabel = (row) => {
    if (row.payMethodName) {
        return row.payMethodName
    }

    if (row.payMethod === 'vbank') {
        return '가상계좌'
    }

    if (row.payMethod) {
        return '신용카드'
    }

    return '-'
}

const getPaymentMethodColor = (payMethod) => {
    if (payMethod === 'vbank') {
        return 'info'
    }

    if (payMethod) {
        return 'primary'
    }

    return 'default'
}

const formatWon = (value) => `${formatNumber(value)}원`

export default function OrderClaims({ role }) {
    const location = useLocation()
    const history = useHistory()
    const initialListStateRef = useRef(getInitialListState(location))
    const initialListState = initialListStateRef.current
    const [loading, setLoading] = useState(true)
    const [totalCount, setTotalCount] = useState(0)
    const [params, setParams] = useState([])
    const [curPage, setCurPage] = useState(initialListState.curPage)
    const offset = 10
    const [openSnackbar, setOpenSnackbar] = useState(initialListState.snackbar.openSnackbar)
    const [severitySnackbar, setSeveritySnackbar] = useState(initialListState.snackbar.severitySnackbar)
    const [messageSnackbar, setMessageSnackbar] = useState(initialListState.snackbar.messageSnackbar)
    const [searchText, setSearchText] = useState(initialListState.searchText)
    const [searchQuery, setSearchQuery] = useState(initialListState.searchQuery)
    const [searchSelect, setSearchSelect] = useState(initialListState.searchSelect)
    const [listErrorMessage, setListErrorMessage] = useState('')

    const getData = useCallback(async () => {
        setLoading(true)
        setListErrorMessage('')

        try {
            const { data } = await API.post('/api/v1/admin/orderClaim/getList', { page: curPage, offset: offset, searchText: searchQuery, searchSelect: searchSelect })
            if (data.code === 200) {
                setTotalCount(data.data?.count || 0)
                setParams(data.data?.rows || [])
                setListErrorMessage('')
            } else if (data.code === 403) {
                history.push('/login')
                return
            } else {
                const errorMessage = data.message || '클레임 목록을 불러오지 못했습니다.'

                setTotalCount(0)
                setParams([])
                setListErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            }
        } catch (_error) {
            const errorMessage = '클레임 목록을 불러오지 못했습니다.'

            setTotalCount(0)
            setParams([])
            setListErrorMessage(errorMessage)
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(errorMessage)
        } finally {
            setLoading(false)
        }
    }, [curPage, history, searchQuery, searchSelect])

    useEffect(() => {
        getData()
    }, [getData])

    useEffect(() => {
        const nextSearch = buildListSearch({ curPage, searchQuery, searchSelect })

        if (location.search !== nextSearch) {
            history.replace({
                pathname: location.pathname,
                search: nextSearch,
            })
        }
    }, [curPage, history, location.pathname, location.search, searchQuery, searchSelect])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    const updateSearchSelect = (e) => {
        setCurPage(1)
        setSearchSelect({
            ...searchSelect,
            [e.target.name]: e.target.value || 0
        })
    }

    const resetFilters = () => {
        if (loading) {
            return
        }

        setCurPage(1)
        setSearchText('')
        setSearchQuery('')
        setSearchSelect({
            ...DEFAULT_SEARCH_SELECT,
        })
    }

    const handleSearchSubmit = (e) => {
        e.preventDefault()
        searchButtonClick()
    }

    const searchButtonClick = () => {
        if (loading) {
            return
        }

        const trimmedSearchText = (searchText || '').trim()

        if (curPage !== 1) {
            setCurPage(1)
        }

        if (searchQuery !== trimmedSearchText) {
            setSearchQuery(trimmedSearchText)
            return
        }

        if (curPage === 1) {
            getData()
        }
    }

    const formatClaimReasonForList = (row) => {
        if (Number(row.claimReasonType) === 3) {
            return '기타사유'
        }

        return row.claimReason || '-'
    }

    // 검색어와 선택 필터가 기본값에서 벗어났는지 판단해 빈 상태 안내 문구를 나눈다.
    const hasActiveFilters = () => {
        if ((searchQuery || '').trim()) {
            return true
        }

        return Object.entries(DEFAULT_SEARCH_SELECT).some(([key, defaultValue]) => {
            const currentValue = searchSelect?.[key] ?? defaultValue

            return String(currentValue) !== String(defaultValue)
        })
    }

    // 테이블 행이 없을 때 오류, 필터 결과 없음, 기본 빈 목록을 구분해서 보여준다.
    const renderEmptyState = () => {
        if (listErrorMessage) {
            return (
                <ListEmptyState
                    variant="error"
                    title="클레임 목록을 불러오지 못했습니다."
                    description={listErrorMessage}
                    action={(
                        <Button type="button" size="small" variant="outlined" onClick={getData} disabled={loading}>
                            다시 조회
                        </Button>
                    )}
                />
            )
        }

        if (hasActiveFilters()) {
            return (
                <ListEmptyState
                    title="조건에 맞는 클레임이 없습니다."
                    description="검색어 또는 필터를 초기화한 뒤 다시 확인해보세요."
                    action={<ResetFilterButton label="필터 초기화" onClick={resetFilters} disabled={loading} />}
                />
            )
        }

        return (
            <ListEmptyState
                title="조회된 클레임 내역이 없습니다."
                description="환불/반품 신청이 발생하면 이 목록에 표시됩니다."
            />
        )
    }

    return (
        <Grid item xs={12}>
            {loading ? (
                <Spiner/>
            ) : (
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    {/* 타이틀 */}
                    <Title>환불/반품 신청 리스트</Title>
                    {/* 검색폼 */}
                    <ListFilterBar>
                        <ListFilterForm onSubmit={handleSearchSubmit} trailing={<ResetFilterButton onClick={resetFilters} disabled={loading} />}>
                            <InputBase
                                sx={LIST_FILTER_SEARCH_FIELD_SX}
                                placeholder="닉네임/시설명"
                                type="search"
                                name="searchText"
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                            />
                            <IconButton type="submit" sx={LIST_FILTER_SEARCH_BUTTON_SX} aria-label="search" disabled={loading}><SearchIcon /></IconButton>
                            <Divider sx={{ height: 28, m: 0.5, marginRight:2 }} orientation="vertical" />
                            <TextField
                                label="신청타입"
                                select
                                name="searchClaimType"
                                value={searchSelect.searchClaimType ?? 0}
                                onChange={updateSearchSelect}
                                size="small"
                                sx={{ minWidth: 150 }}
                            >
                                <MenuItem value={0}>전체</MenuItem>
                                {/* <MenuItem value={1}>취소</MenuItem> */}
                                <MenuItem value={2}>환불</MenuItem>
                                <MenuItem value={3}>반품</MenuItem>
                            </TextField>
                            <TextField
                                label="처리상태"
                                select
                                name="searchStatus"
                                value={searchSelect.searchStatus ?? 0}
                                onChange={updateSearchSelect}
                                size="small"
                                sx={{ minWidth: 150 }}
                            >
                                <MenuItem value={0}>전체</MenuItem>
                                <MenuItem value={1}>접수</MenuItem>
                                {/* <MenuItem value={2}>처리중</MenuItem> */}
                                <MenuItem value={3}>완료</MenuItem>
                                <MenuItem value={4}>거절</MenuItem>
                            </TextField>
                        </ListFilterForm>
                    </ListFilterBar>
                    
                    {/* 테이블 */}
                    <ListTableContainer>
                    <Table size="small" sx={{ minWidth: 1400 }}>
                        <caption>Total Count: {totalCount}</caption>
                        <TableHead>
                            <TableRow>
                                <TableCell>No</TableCell>
                                <TableCell>클레임번호<br/>주문번호</TableCell>
                                <TableCell>회원</TableCell>
                                <TableCell>연락처</TableCell>
                                <TableCell>처리<br/>상태</TableCell>
                                <TableCell>신청<br/>타입</TableCell>
                                <TableCell>결제<br/>수단</TableCell>
                                <TableCell align="right">클레임<br/>금액</TableCell>
                                <TableCell align="right">클레임<br/>수량</TableCell>
                                <TableCell align="right">배송비<br/>차감</TableCell>
                                <TableCell align="right">환불<br/>금액</TableCell>
                                <TableCell>복구 예상</TableCell>
                                <TableCell>사유</TableCell>
                                <TableCell>신청일<br/>처리일</TableCell>
                                <TableCell align="right">Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {params.length > 0 ? params.map((row) => (
                                <TableRow key={row.orderClaimId} hover>
                                    <TableCell>{row.rownum}</TableCell>
                                    <TableCell>
                                        <Box sx={{ fontWeight: 700 }}>{row.orderClaimNo || '-'}</Box>
                                        <Box sx={{ color: 'text.secondary' }}>{row.orderId || '-'}</Box>
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ fontWeight: 700 }}>{row.username || '-'}</Box>
                                        <Box sx={{ color: 'text.secondary' }}>{row.nickName || '-'} / {row.userId || '-'}</Box>
                                    </TableCell>
                                    <TableCell>
                                        <Box>{row.phone || '-'}</Box>
                                        <Box sx={{ color: 'text.secondary', wordBreak: 'break-all' }}>{row.email || '-'}</Box>
                                    </TableCell>
                                    <TableCell>
                                        <StatusChip status={row.status} config={ORDER_CLAIM_STATUS_CHIP} label={row.statusName} />
                                    </TableCell>
                                    <TableCell>
                                        <StatusChip
                                            label={row.claimTypeName || '-'}
                                            color={getClaimTypeColor(row.claimType)}
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <StatusChip
                                            label={getPaymentMethodLabel(row)}
                                            color={getPaymentMethodColor(row.payMethod)}
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell align="right">{formatWon(row.totalClaimAmount)}</TableCell>
                                    <TableCell align="right">{formatNumber(row.totalClaimQuantity)}</TableCell>
                                    <TableCell align="right">{formatWon(row.totalShippingAmount * -1)}</TableCell>
                                    <TableCell align="right">{formatWon(row.refundAmount)}</TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                            <StatusChip
                                                label={row.expectedCouponRestore ? '쿠폰 복구 예정' : '쿠폰 미복구'}
                                                color={row.expectedCouponRestore ? 'success' : 'default'}
                                            />
                                            <StatusChip
                                                label={`포인트 +${formatNumber(row.expectedPointRestore)}P`}
                                                color={Number(row.expectedPointRestore || 0) > 0 ? 'primary' : 'default'}
                                            />
                                        </Box>
                                    </TableCell>
                                    <TableCell className="listTableLimitText">{formatClaimReasonForList(row)}</TableCell>
                                    <TableCell>
                                        <Box>{row.regTime || '-'}</Box>
                                        <Box sx={{ color: 'text.secondary' }}>{row.processTime || '-'}</Box>
                                    </TableCell>
                                    <TableCell align="right">
                                        <ListActionGroup>
                                            <EditIcon path={'orderClaim'} id={row.orderClaimId} search={location.search} curPage={curPage} searchText={searchQuery} searchSelect={searchSelect} disabled={loading}/>
                                        </ListActionGroup>
                                    </TableCell>
                                </TableRow>
                            )) : (
                                <TableRow>
                                    <TableCell colSpan={15}>
                                        {renderEmptyState()}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                    </ListTableContainer>
                    {totalCount > 0 && <Pagination curPage={curPage} offset={offset} totalCount={totalCount} setCurPage={setCurPage} />}
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Paper>
            )}
        </Grid>
    )
}
